import { useQuery } from '@tanstack/react-query';
import type {
  BalanceSplitResponse,
  CommandCenterKpis,
  CompanySummaryResponse,
  ExpenseBreakdownResponse,
  TimeSeriesResponse,
} from '@accounting/shared';
import { api } from '../lib/api';

function key(endpoint: string, ids: string[], from: string, to: string) {
  return ['command-center', endpoint, [...ids].sort().join(','), from, to] as const;
}

function useCC<T>(
  endpoint: string,
  ids: string[],
  from: string,
  to: string,
) {
  return useQuery({
    queryKey: key(endpoint, ids, from, to),
    enabled: ids.length > 0 && !!from && !!to,
    queryFn: () =>
      api.get<T>(`/command-center/${endpoint}`, {
        companyIds: ids.join(','),
        from,
        to,
      }),
  });
}

/** Fires every Command Center query in parallel. */
export function useCommandCenterData(ids: string[], from: string, to: string) {
  const kpis = useCC<CommandCenterKpis>('kpis', ids, from, to);
  const revenueTrend = useCC<TimeSeriesResponse>('revenue-trend', ids, from, to);
  const netIncomeTrend = useCC<TimeSeriesResponse>('net-income-trend', ids, from, to);
  const cashTrend = useCC<TimeSeriesResponse>('cash-trend', ids, from, to);
  const expenseBreakdown = useCC<ExpenseBreakdownResponse>('expense-breakdown', ids, from, to);
  const companySummary = useCC<CompanySummaryResponse>('company-summary', ids, from, to);
  const balanceSplit = useCC<BalanceSplitResponse>('balance-split', ids, from, to);

  return {
    kpis,
    revenueTrend,
    netIncomeTrend,
    cashTrend,
    expenseBreakdown,
    companySummary,
    balanceSplit,
    isLoading:
      kpis.isLoading ||
      revenueTrend.isLoading ||
      netIncomeTrend.isLoading ||
      cashTrend.isLoading ||
      expenseBreakdown.isLoading ||
      companySummary.isLoading ||
      balanceSplit.isLoading,
  };
}
